import { useEffect, useRef, useState } from 'react';

/**
 * @file Subscribes React to `engine.bus` without ever putting the engine
 * itself in state. The engine can emit far faster than 10Hz (some channels
 * are per-frame); this hook coalesces bursts into at most one `setState` per
 * `throttle` ms (trailing-edge), which is what keeps HUD components from
 * thrashing the render tree.
 */

/**
 * @template T
 * @param {{bus?: import('../../engine/types.js').EventBus}|null|undefined} engine
 *   Anything with a `.bus` — normally the Engine instance, but any duck-typed
 *   object works. `null`/`undefined` just means "not subscribed".
 * @param {string} event Bus channel name, e.g. `'time'`, `'player:hp'`.
 * @param {{throttle?: number, initial?: T|null}} [opts]
 * @returns {T|null} The latest payload, or `initial` (default `null`) before
 *   the first event / whenever there is no engine to listen to.
 */
export function useEngineBus(engine, event, opts = {}) {
  const { throttle = 100, initial = null } = opts;
  const [value, setValue] = useState(initial);

  const pendingRef = useRef(undefined);
  const timerRef = useRef(null);
  const lastFlushRef = useRef(0);

  useEffect(() => {
    // New engine/event identity: drop whatever the old subscription had
    // in flight and start clean, otherwise a stale payload from a disposed
    // engine could flash in right as a new one is subscribed.
    setValue(initial);
    pendingRef.current = undefined;
    lastFlushRef.current = 0;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    const bus = engine?.bus;
    if (!bus?.on) return undefined;

    const flush = () => {
      timerRef.current = null;
      lastFlushRef.current = Date.now();
      if (pendingRef.current !== undefined) {
        const next = pendingRef.current;
        pendingRef.current = undefined;
        setValue(next);
      }
    };

    const handler = (payload) => {
      pendingRef.current = payload;
      const elapsed = Date.now() - lastFlushRef.current;
      if (elapsed >= throttle) {
        flush();
      } else if (!timerRef.current) {
        timerRef.current = setTimeout(flush, throttle - elapsed);
      }
    };

    const unsubscribe = bus.on(event, handler);

    return () => {
      unsubscribe?.();
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    // `initial` is a per-call constant in every caller in this codebase; only
    // engine/event/throttle identity should restart the subscription.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine, event, throttle]);

  return value;
}
